import type { Reward } from "../types/client";

export interface SpinPlan {
  finalRotation: number;
  selectedIndex: number;
}

export function selectReward(rewards: Reward[], randomValue: number): Reward {
  if (randomValue < 0 || randomValue >= 1 || !Number.isFinite(randomValue)) {
    throw new Error("Random value must be between 0 (inclusive) and 1 (exclusive)." );
  }

  const enabledRewards = rewards.filter((reward) => reward.enabled);
  if (enabledRewards.length === 0) {
    throw new Error("No enabled rewards are available.");
  }

  let cumulativeProbability = 0;
  for (const reward of enabledRewards) {
    cumulativeProbability += reward.probability;
    if (randomValue < cumulativeProbability) {
      return reward;
    }
  }

  // Protect against tiny IEEE-754 rounding gaps after configuration validation.
  return enabledRewards[enabledRewards.length - 1];
}

export function createSpinPlan(
  currentRotation: number,
  selectedIndex: number,
  segmentCount: number,
  randomValue: number,
  extraTurns = 6,
): SpinPlan {
  if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || selectedIndex >= segmentCount) {
    throw new Error("Selected wheel index is out of range.");
  }
  if (segmentCount < 2) {
    throw new Error("A wheel requires at least two segments.");
  }

  const segmentAngle = 360 / segmentCount;
  const segmentCenter = (selectedIndex + 0.5) * segmentAngle;
  const safeJitter = (Math.min(Math.max(randomValue, 0), 0.999999) - 0.5) * segmentAngle * 0.5;
  const desiredAngle = (360 - segmentCenter + safeJitter + 360) % 360;
  const normalizedCurrent = ((currentRotation % 360) + 360) % 360;
  const forwardDelta = (desiredAngle - normalizedCurrent + 360) % 360;

  return {
    finalRotation: currentRotation + Math.max(1, extraTurns) * 360 + forwardDelta,
    selectedIndex,
  };
}

export function secureRandomUnit(): number {
  if (globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    return values[0] / 4_294_967_296;
  }
  return Math.random();
}
